/**
 * Bana Wishlist System v3.1
 * - Guest: localStorage (no login required)
 * - Logged in: localStorage + synced across sessions
 * - Header badge count (static span, updated via JS)
 * - Red heart + toast popup on add/remove
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'bana_wishlist_v3';

  // ── Storage helpers ──────────────────────────────────────────
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  function saveWishlist(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
    catch {}
    updateAllUI();
  }

  function isInWishlist(productId) {
    return getWishlist().some(item => String(item.id) === String(productId));
  }

  function addToWishlist(item) {
    const list = getWishlist();
    if (!list.some(i => String(i.id) === String(item.id))) {
      list.push(item);
      saveWishlist(list);
    }
  }

  function removeFromWishlist(productId) {
    saveWishlist(getWishlist().filter(i => String(i.id) !== String(productId)));
  }

  function toggleWishlist(item) {
    if (isInWishlist(item.id)) { removeFromWishlist(item.id); return false; }
    else { addToWishlist(item); return true; }
  }

  // ── Header badge ─────────────────────────────────────────────
  function updateHeaderBadge() {
    const count = getWishlist().length;

    // Target the static badge span we added in the Liquid template
    let badge = document.querySelector('[data-bana-wishlist-count]');

    // Fallback: create it dynamically if the Liquid span isn't there yet
    if (!badge) {
      const wishlistLink = document.querySelector('[data-bana-wishlist-link]');
      if (wishlistLink) {
        wishlistLink.style.cssText += ';position:relative!important;overflow:visible!important;';
        badge = document.createElement('span');
        badge.setAttribute('data-bana-wishlist-count', '');
        badge.style.cssText =
          'display:none;position:absolute;top:-7px;right:-9px;min-width:17px;height:17px;' +
          'padding:0 4px;border-radius:999px;background:#721514;color:#fff;' +
          'font:700 9px/17px Inter,sans-serif;text-align:center;pointer-events:none;z-index:10;';
        wishlistLink.appendChild(badge);
      }
    }

    if (!badge) return;

    // Make sure parent has correct positioning
    const parent = badge.parentElement;
    if (parent) {
      const ps = window.getComputedStyle(parent);
      if (ps.position === 'static') parent.style.position = 'relative';
      if (ps.overflow === 'hidden') parent.style.overflow = 'visible';
    }

    if (count > 0) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.style.display = 'block';
      // Animate badge pop when count changes
      badge.style.transform = 'scale(1.3)';
      setTimeout(() => { badge.style.transform = 'scale(1)'; badge.style.transition = 'transform 0.2s ease'; }, 10);
    } else {
      badge.style.display = 'none';
    }
  }

  // ── Toast notification ───────────────────────────────────────
  function showToast(message, isAdd) {
    const existing = document.getElementById('bana-wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'bana-wishlist-toast';
    toast.style.cssText =
      'position:fixed;bottom:90px;left:50%;transform:translateX(-50%) translateY(12px);' +
      'background:#1a1a1a;color:#fff;font:500 13px/1 "DM Sans",Inter,sans-serif;' +
      'padding:12px 20px;border-radius:50px;display:flex;align-items:center;gap:8px;' +
      'z-index:99999;opacity:0;transition:opacity 0.25s ease,transform 0.25s ease;' +
      'pointer-events:none;white-space:nowrap;box-shadow:0 4px 20px rgba(0,0,0,0.25);';

    toast.innerHTML = isAdd
      ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444"><path d="M12 19.15s-6.85-4.35-8.12-8.82C2.98 7.2 4.9 4.75 7.65 4.75c1.64 0 3.08.86 4.35 2.35 1.27-1.49 2.71-2.35 4.35-2.35 2.75 0 4.67 2.45 3.77 5.58C18.85 14.8 12 19.15 12 19.15Z"/></svg>' + message
      : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" stroke-width="2"><path d="M12 19.15s-6.85-4.35-8.12-8.82C2.98 7.2 4.9 4.75 7.65 4.75c1.64 0 3.08.86 4.35 2.35 1.27-1.49 2.71-2.35 4.35-2.35 2.75 0 4.67 2.45 3.77 5.58C18.85 14.8 12 19.15 12 19.15Z"/></svg>' + message;

    document.body.appendChild(toast);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    }));
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(8px)';
      setTimeout(() => toast.remove(), 300);
    }, 2200);
  }

  // ── Update all heart buttons on page ────────────────────────
  function updateAllHearts() {
    document.querySelectorAll('[data-bana-product-wishlist]').forEach(btn => {
      const id = btn.getAttribute('data-product-id');
      if (!id) return;
      const active = isInWishlist(id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      applyHeartStyle(btn, active);
    });
  }

  function applyHeartStyle(btn, active) {
    const svg = btn.querySelector('svg');
    if (!svg) return;
    if (active) {
      svg.style.fill = '#721514';
      svg.style.stroke = '#721514';
      svg.style.color = '#721514';
    } else {
      svg.style.fill = 'none';
      svg.style.stroke = 'currentColor';
      svg.style.color = '';
    }
  }

  function updateAllUI() {
    updateHeaderBadge();
    updateAllHearts();
    renderWishlistPage();
  }

  // ── Handle wishlist button click ─────────────────────────────
  function handleWishlistClick(btn) {
    const id    = btn.getAttribute('data-product-id');
    const title = btn.getAttribute('data-product-title') || '';
    const url   = btn.getAttribute('data-product-url') || '';
    const image = btn.getAttribute('data-product-image') || '';
    const price = btn.getAttribute('data-product-price') || '';
    if (!id) return;

    const added = toggleWishlist({ id, title, url, image, price });

    // Instant visual feedback
    btn.classList.toggle('is-active', added);
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    applyHeartStyle(btn, added);

    // Bounce animation
    btn.style.transition = 'transform 0.18s ease';
    btn.style.transform = 'scale(1.4)';
    setTimeout(() => { btn.style.transform = 'scale(1)'; }, 180);

    showToast(added ? 'Added to Wishlist' : 'Removed from Wishlist', added);
  }

  // ── Wishlist page renderer ───────────────────────────────────
  function renderWishlistPage() {
    const grid = document.querySelector('[data-bana-wishlist-grid]');
    if (!grid) return;
    const emptyEl = grid.querySelector('[data-bana-wishlist-empty]');
    const list = getWishlist();
    grid.querySelectorAll('.bana-wl-card').forEach(c => c.remove());

    if (list.length === 0) {
      if (emptyEl) emptyEl.style.display = '';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    list.forEach(item => {
      const card = document.createElement('div');
      card.className = 'bana-wl-card';
      card.style.cssText = 'background:#fff;border:1px solid #efe8df;border-radius:12px;overflow:hidden;position:relative;';
      card.innerHTML =
        '<a href="' + item.url + '" style="display:block;text-decoration:none;color:inherit;">' +
          (item.image
            ? '<img src="' + item.image + '" alt="' + item.title + '" style="width:100%;aspect-ratio:3/4;object-fit:cover;" loading="lazy">'
            : '<div style="width:100%;aspect-ratio:3/4;background:#f5f0eb;"></div>') +
          '<div style="padding:14px 16px 16px;">' +
            '<p style="margin:0 0 4px;font-size:12px;color:#888;font-family:DM Sans,Inter,sans-serif;">Bana</p>' +
            '<h3 style="margin:0 0 8px;font-size:14px;font-family:DM Sans,Inter,sans-serif;font-weight:500;line-height:1.4;">' + item.title + '</h3>' +
            '<p style="margin:0;font-size:13px;font-weight:700;font-family:DM Sans,Inter,sans-serif;">' + item.price + '</p>' +
          '</div>' +
        '</a>' +
        '<button data-bana-product-wishlist data-product-id="' + item.id + '" data-product-title="' + item.title + '" data-product-url="' + item.url + '" data-product-image="' + item.image + '" data-product-price="' + item.price + '" class="bana-wishlist-button bana-wishlist-button--card is-active" aria-label="Remove from wishlist" aria-pressed="true" style="position:absolute;top:10px;right:10px;background:transparent;border:none;cursor:pointer;padding:4px;">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" style="fill:#721514;stroke:#721514;stroke-width:1.8;" focusable="false" aria-hidden="true"><path d="M12 19.15s-6.85-4.35-8.12-8.82C2.98 7.2 4.9 4.75 7.65 4.75c1.64 0 3.08.86 4.35 2.35 1.27-1.49 2.71-2.35 4.35-2.35 2.75 0 4.67 2.45 3.77 5.58C18.85 14.8 12 19.15 12 19.15Z"/></svg>' +
        '</button>';
      grid.appendChild(card);
    });
  }

  // ── Event listener ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-bana-product-wishlist]');
    if (btn) { e.preventDefault(); e.stopPropagation(); handleWishlistClick(btn); }
  });

  // ── Init ─────────────────────────────────────────────────────
  function init() { updateAllUI(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', updateAllUI);

})();
